import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Grid, Typography, Alert, Divider, Paper, TextField, Button, ButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TrendingUp, DoneAll, ErrorOutline, CalendarMonth } from '@mui/icons-material';
import { detectionApi } from '../services/api';
import { DetectionStatsResponse } from '../types/api';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ruRU } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const COLORS = ['#FF9800', '#00C49F', '#FFBB28', '#FF8042', '#ab47bc', '#26a69a'];

const StatisticsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [stats, setStats] = useState<DetectionStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [to, setTo] = useState<string | undefined>(undefined);
  // state removed (revert)

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await detectionApi.getStats(from, to);
        setStats(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить статистику');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [from, to]);

  const totalData = stats
    ? [
        { name: 'Успешные', value: stats.success },
        { name: 'Неуспешные', value: stats.failed },
      ]
    : [];
  const totalSum = totalData.reduce((sum, item) => sum + (item.value || 0), 0);
  const nonZeroSlices = totalData.filter((d) => (d.value || 0) > 0).length;

  const methodData = stats
    ? [
        { name: 'Всего', success: stats.success },
        ...stats.byMethod
          .filter((m) => (m.method || '').trim().length > 0)
          .map((m) => ({ name: m.method, success: m.success }))
      ]
    : [];

  const getMethodLabel = (method: string): string => {
    const map: Record<string, string> = {
      'Word': 'Текстовый шаблон',
      'Voice': 'Голосовой шаблон',
      'Sound': 'Звуковой шаблон',
      'Synthetic': 'Синтетический голос',
      'Acoustic': 'Акустический шаблон',
      'Всего': 'Всего',
    };
    return map[method] || method;
  };

  const RotatedTick: React.FC<any> = ({ x, y, payload }) => {
    const label = getMethodLabel(String(payload?.value ?? ''));
    return (
      <g transform={`translate(${x},${y})`}>
        <text dy={16} textAnchor="end" fill="currentColor" transform="rotate(-15)">
          {label}
        </text>
      </g>
    );
  };

  // default legend

  dayjs.locale('ru');

  return (
    <Box sx={{ height: { xs: 'auto', md: 'calc(100vh - 120px)' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">
          Статистика детекций
        </Typography>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Контролы периода */}
      <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth />
            <Typography variant="subtitle1">Период</Typography>
          </Box>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ru"
            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
          >
            <DatePicker
              label="От"
              format="DD.MM.YYYY"
              value={from ? dayjs(from) : null}
              onChange={(val: any) => {
                if (!val) { setFrom(undefined); return; }
                setFrom(val.startOf('day').toDate().toISOString());
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="До"
              format="DD.MM.YYYY"
              value={to ? dayjs(to) : null}
              onChange={(val: any) => {
                if (!val) { setTo(undefined); return; }
                setTo(val.endOf('day').toDate().toISOString());
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          <Box sx={{ flexGrow: 1 }} />
          <ButtonGroup variant="outlined" size="small">
            <Button onClick={() => {
              const toDate = new Date();
              const fromDate = new Date();
              fromDate.setDate(toDate.getDate() - 7);
              setFrom(fromDate.toISOString());
              setTo(toDate.toISOString());
            }}>7 дней</Button>
            <Button onClick={() => {
              const toDate = new Date();
              const fromDate = new Date();
              fromDate.setDate(toDate.getDate() - 30);
              setFrom(fromDate.toISOString());
              setTo(toDate.toISOString());
            }}>30 дней</Button>
            <Button onClick={() => { setFrom(undefined); setTo(undefined); }}>Сброс</Button>
          </ButtonGroup>
        </Box>
      </Paper>

      {/* KPI карточки */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #ffb74d 0%, #fb8c00 100%)', color: '#fff' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp />
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>Всего</Typography>
                  <Typography variant="h4">{stats?.total ?? 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #26a69a 0%, #2e7d32 100%)', color: '#fff' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DoneAll />
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>Успешные</Typography>
                  <Typography variant="h4">{stats?.success ?? 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)', color: '#fff' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ErrorOutline />
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>Неуспешные</Typography>
                  <Typography variant="h4">{stats?.failed ?? 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      {/* Зона графиков занимает остаток ширины (адаптивная) */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, px: { xs: 1.5, sm: 2 } }}>
        <Card sx={{ flex: 1.2, minHeight: { xs: 320, md: 0 }, display: 'flex', flexDirection: 'column' }}>
          <CardHeader title="Итого" subheader={`Всего: ${stats?.total ?? 0}`} sx={{ pb: 0 }} />
          <CardContent sx={{ flex: 1, pt: 0, minHeight: 0 }}>
            <Box sx={{ height: { xs: 280, md: '100%' }, width: '100%', minHeight: 0 }}>
              {totalSum === 0 ? (
                <Box sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography>Нет данных за выбранный период</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
  <PieChart margin={{ top: 24, right: 24, bottom: 16, left: 24 }}>
    <defs>
      <linearGradient id="successGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#26a69a" />
        <stop offset="100%" stopColor="#2e7d32" />
      </linearGradient>
      <linearGradient id="failedGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ef5350" />
        <stop offset="100%" stopColor="#d32f2f" />
      </linearGradient>
    </defs>
    <Pie
      dataKey="value"
      data={totalData}
      cx="50%"
      cy="50%"
      innerRadius="35%"
      outerRadius="90%"
      label={nonZeroSlices > 1}
      labelLine
    >
      {totalData.map((entry, index) => (
        <Cell key={`cell-${index}`} fill={index === 0 ? 'url(#successGrad)' : 'url(#failedGrad)'} />
      ))}
    </Pie>
    <Tooltip 
      contentStyle={{ 
        background: '#151517', 
        border: '1px solid #333', 
        color: '#fff' 
      }} 
      formatter={(value: number, name: string) => [value, name]}
      itemStyle={{ color: '#fff' }} // ← Это исправление
    />
    <Legend verticalAlign="bottom" />
  </PieChart>
</ResponsiveContainer>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1.8, minHeight: { xs: 320, md: 0 }, display: 'flex', flexDirection: 'column' }}>
          <CardHeader title="По методам" sx={{ pb: 0 }} />
          <CardContent sx={{ flex: 1, pt: 0, minHeight: 0 }}>
            <Box sx={{ height: { xs: 280, md: '100%' }, width: '100%', minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={methodData} margin={{ top: 16, right: 48, left: 24, bottom: 24 }}>
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffb74d" />
                      <stop offset="100%" stopColor="#fb8c00" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" interval={0} tick={false} height={0} />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: '#151517', border: '1px solid #333', color: '#fff' }}
                    labelFormatter={(label) => getMethodLabel(String(label))}
                    formatter={(value: number) => [value, 'Всего']}
                  />
                  <Bar dataKey="success" name="Успешные" fill="url(#barGrad)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default StatisticsPage;